import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Wallet, LogOut, LayoutDashboard, Shield, LifeBuoy, AlertTriangle, Star } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";
import { useWallet } from "../context/WalletContext";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const DISCLAIMER =
  "Les guidances sont proposées à des fins de réflexion personnelle et de divertissement. Elles ne remplacent jamais un avis médical, psychologique, juridique ou financier et ne garantissent aucun résultat futur.";

function EmergencyReportDialog() {
  const [reason, setReason] = useState("");
  const [desc, setDesc] = useState("");
  const [open, setOpen] = useState(false);
  const { user } = useAuth();

  const submit = async () => {
    if (!user) { toast.error("Connectez-vous pour signaler."); return; }
    try {
      await api.report({ target_type: "user", target_id: "plateforme", reason: reason || "Signalement", description: desc });
      toast.success("Signalement transmis à la modération.");
      setOpen(false); setReason(""); setDesc("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="open-report-btn" className="inline-flex items-center gap-1.5 text-xs text-celeste-lilas hover:text-celeste-or transition-colors">
          <AlertTriangle className="h-3.5 w-3.5" /> Signaler / Urgence
        </button>
      </DialogTrigger>
      <DialogContent className="glass-strong text-celeste-ivoire border-celeste-or/20">
        <DialogHeader>
          <DialogTitle className="font-serif text-celeste-or">Signalement & aide d'urgence</DialogTitle>
        </DialogHeader>
        <div className="rounded-2xl border border-red-400/30 bg-red-500/10 p-4 text-sm" data-testid="emergency-info">
          <p className="font-semibold text-red-200">En cas de danger immédiat pour vous ou autrui :</p>
          <ul className="mt-2 space-y-1 text-celeste-lilas">
            <li>• <strong>112</strong> — Urgences européennes</li>
            <li>• <strong>15</strong> — SAMU (urgence médicale)</li>
            <li>• <strong>3114</strong> — Numéro national de prévention du suicide (24h/24, gratuit)</li>
            <li>• <strong>17</strong> — Police / Gendarmerie</li>
          </ul>
        </div>
        <div className="space-y-3">
          <Input data-testid="report-reason-input" placeholder="Motif du signalement" value={reason} onChange={(e) => setReason(e.target.value)} className="bg-celeste-night/60 border-celeste-violet/30" />
          <Textarea data-testid="report-desc-input" placeholder="Décrivez la situation…" value={desc} onChange={(e) => setDesc(e.target.value)} className="bg-celeste-night/60 border-celeste-violet/30" />
        </div>
        <DialogFooter>
          <Button data-testid="submit-report-btn" onClick={submit} className="gold-btn font-semibold">Envoyer le signalement</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const { balance } = useWallet();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const dashPath = user?.role === "admin" ? "/admin" : user?.role === "expert" ? "/expert" : "/tableau-de-bord";

  const nav = [
    { to: "/experts", label: "Experts" },
    { to: "/comment-ca-marche", label: "Comment ça marche" },
    { to: "/tarifs", label: "Tarifs" },
    { to: "/faq", label: "FAQ" },
  ];

  return (
    <div className="relative z-10 flex min-h-screen flex-col">
      <div className="w-full bg-celeste-prune/80 backdrop-blur border-b border-celeste-or/15 text-center text-[11px] sm:text-xs text-celeste-lilas px-4 py-1.5" data-testid="age-banner">
        Plateforme réservée aux personnes majeures (18+). Consultations à visée de réflexion et de divertissement.
      </div>

      <header className="sticky top-0 z-40 glass border-b border-celeste-or/15">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" data-testid="nav-home-link"><Logo /></Link>

          <nav className="hidden items-center gap-7 lg:flex">
            {nav.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.to.replace(/\//g, "")}-link`}
                className="text-sm text-celeste-lilas hover:text-celeste-or transition-colors">{n.label}</Link>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            {user ? (
              <>
                <Link to="/credits" data-testid="wallet-balance-chip" className="flex items-center gap-2 rounded-full border border-celeste-or/30 bg-celeste-night/50 px-3 py-1.5 text-sm text-celeste-or hover:border-celeste-or transition-colors">
                  <Wallet className="h-4 w-4" /> {balance != null ? `${balance} crédits` : "…"}
                </Link>
                <Button data-testid="nav-dashboard-btn" variant="ghost" className="text-celeste-lilas hover:text-celeste-ivoire" onClick={() => navigate(dashPath)}>
                  {user.role === "admin" ? <Shield className="mr-1.5 h-4 w-4" /> : <LayoutDashboard className="mr-1.5 h-4 w-4" />}
                  Mon espace
                </Button>
                <Button data-testid="nav-logout-btn" variant="ghost" size="icon" className="text-celeste-lilas" onClick={logout}><LogOut className="h-4 w-4" /></Button>
              </>
            ) : (
              <>
                <Button data-testid="nav-login-btn" variant="ghost" className="text-celeste-lilas hover:text-celeste-ivoire" onClick={() => navigate("/connexion")}>Connexion</Button>
                <Button data-testid="nav-register-btn" className="gold-btn font-semibold rounded-full" onClick={() => navigate("/inscription")}>Rejoindre</Button>
              </>
            )}
          </div>

          <button data-testid="mobile-menu-btn" className="lg:hidden text-celeste-ivoire" onClick={() => setMenuOpen((v) => !v)}>
            {menuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {menuOpen && (
          <div className="lg:hidden glass-strong border-t border-celeste-or/15 px-4 py-4 space-y-3" data-testid="mobile-menu">
            {nav.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setMenuOpen(false)} className="block text-celeste-lilas">{n.label}</Link>
            ))}
            <div className="h-px bg-celeste-or/15" />
            {user ? (
              <>
                <Link to="/credits" onClick={() => setMenuOpen(false)} className="block text-celeste-or">Portefeuille : {balance ?? "…"} crédits</Link>
                <Link to={dashPath} onClick={() => setMenuOpen(false)} className="block text-celeste-lilas">Mon espace</Link>
                <button onClick={() => { logout(); setMenuOpen(false); }} className="block text-celeste-lilas">Déconnexion</button>
              </>
            ) : (
              <>
                <Link to="/connexion" onClick={() => setMenuOpen(false)} className="block text-celeste-lilas">Connexion</Link>
                <Link to="/inscription" onClick={() => setMenuOpen(false)} className="block text-celeste-or">Rejoindre</Link>
              </>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="relative z-10 mt-16 border-t border-celeste-or/15 bg-celeste-night/70 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
          <div className="grid gap-8 md:grid-cols-4">
            <div className="md:col-span-2">
              <Logo />
              <p className="mt-4 max-w-md text-sm text-celeste-lilas/80">
                Plateforme de mise en relation avec des praticiens indépendants de guidance : médiums, cartomanciens, tarologues et astrologues.
              </p>
              <div className="mt-4 flex items-center gap-4">
                <EmergencyReportDialog />
                <Link to="/assistance" data-testid="footer-support-link" className="inline-flex items-center gap-1.5 text-xs text-celeste-lilas hover:text-celeste-or">
                  <LifeBuoy className="h-3.5 w-3.5" /> Assistance
                </Link>
              </div>
            </div>
            <div>
              <h4 className="mb-3 text-sm font-semibold text-celeste-or">Découvrir</h4>
              <ul className="space-y-2 text-sm text-celeste-lilas/80">
                <li><Link to="/experts">Annuaire des experts</Link></li>
                <li><Link to="/comment-ca-marche">Comment ça marche</Link></li>
                <li><Link to="/tarifs">Tarifs & crédits</Link></li>
                <li><Link to="/faq">Questions fréquentes</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="mb-3 text-sm font-semibold text-celeste-or">Informations légales</h4>
              <ul className="space-y-2 text-sm text-celeste-lilas/80">
                <li><Link to="/cgu" data-testid="footer-cgu-link">Conditions générales</Link></li>
                <li><Link to="/confidentialite" data-testid="footer-privacy-link">Confidentialité (RGPD)</Link></li>
                <li><Link to="/mentions-legales" data-testid="footer-legal-link">Mentions légales</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-10 rounded-2xl border border-celeste-or/15 bg-celeste-prune/30 p-4 text-xs text-celeste-lilas/80" data-testid="disclaimer-footer">
            <div className="flex items-start gap-2">
              <Star className="mt-0.5 h-4 w-4 shrink-0 text-celeste-or" />
              <p>{DISCLAIMER}</p>
            </div>
          </div>
          <p className="mt-6 text-center text-xs text-celeste-lilas/50">
            © {new Date().getFullYear()} Guidance Céleste Live · Essences Céleste. Nouvelle plateforme — ouverture progressive.
          </p>
        </div>
      </footer>
    </div>
  );
}
